<template>
  <RsPartialNavigationContainer>
    <RsPartialNavigationTree highlight hide-expand-icon expanded class="remove-truncated-text">
      <template #header>
        <div v-if="user.isSuperUser" class="d-flex dropdown-client-info pt-2">
          <div>
            <img class="d-block mt-2" :src="logo" />
          </div>
          <div class="pl-2 flex-grow-1">
            <div class="font-weight-bold">
              {{ user.client.name }}
            </div>
            <div>
              <div class="text-sm">
                <span class="font-weight-bold">ID:</span>&nbsp;{{ user.client.id }} - {{ user.client.clientKey }}
              </div>
              <div v-if="user.client.website" class="text-sm font-weight-bold">
                Website: <a :href="$utils.formatUrl(user.client.website)" target="_blank">
                  {{ user.client.website }}
                </a>
              </div>
              <div>
                <RouterLink to="/internal-page" @click="$emit('hideDropdown')">
                  <span class="rs-text-primary font-weight-bold">More Company Info</span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="d-flex align-items-center dropdown-client-info pt-2"
          :title="user.client.name"
        >
          <div>
            <img class="d-block" :src="logo" />
          </div>
          <div class="pl-2 flex-grow-1">
            <div class="font-weight-bold title mb-0">
              {{ user.client.name }}
            </div>
          </div>
        </div>
      </template>

      <RsPartialNavigationTreeBranch v-if="$rsGlobals.$userHasAccess('company')" class="pt-2 pl-3">
        <RsPartialNavigationTree
          highlight
          title="Company Settings"
          :expanded="expanded === 'settings'"
          :active="routeStartsWith('/settings/co')"
          @click="handleExpand('settings')"
        >
          <RsPartialNavigationTreeBranch
            title="Company Profile"
            :to="{ name: 'SettingsCompany' }"
            :active="routeEndsWith('/settings/company')"
          />
          <RsPartialNavigationTreeBranch
            title="Contact Details"
            :to="{ name: 'SettingsContact' }"
            :active="routeEndsWith('/settings/contact')"
          />
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch v-if="$rsGlobals.$userHasAccess('client_settings')" class="pl-3">
        <RsPartialNavigationTree
          highlight
          title="User Management"
          :active="routeStartsWith('/settings/user-management')"
          :expanded="expanded === 'user_management'"
          @click="handleExpand('user_management')"
        >
          <RsPartialNavigationTreeBranch
            title="User Groups"
            :to="{ name: 'SettingsUserGroups' }"
            :active="routeStartsWith('/settings/user-management/user-groups')"
          />
          <RsPartialNavigationTreeBranch
            title="Users"
            :to="{ name: 'SettingsUsers' }"
            :active="routeStartsWith('/settings/user-management/users')"
          />
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch v-if="$rsGlobals.$userHasAccess('accounting')" class="pl-3">
        <RsPartialNavigationTree
          highlight
          title="Accounting"
          :expanded="expanded === 'accounting'"
          :active="routeIncludes('/outstanding-invoices') || routeIncludes('/billing') || routeIncludes('/accounting')"
          @click="handleExpand('accounting')"
        >
          <RsPartialNavigationTreeBranch
            title="Billing"
            :to="{ name: 'SettingsBilling' }"
            :active="routeIncludes('/settings/billing')"
          />
          <RsPartialNavigationTreeBranch
            :to="{ name: 'SettingsInvoices' }"
            title="Invoices"
            :active="routeIncludes('/settings/invoices')"
          />
          <RsPartialNavigationTreeBranch
            v-if="$rsGlobals.$currentUser.isSuperUser"
            :to="{ name: 'SettingsInvoicing' }"
            title="Invoice Management (SuperAdmin)"
            :active="routeIncludes('/settings/invoicing')"
          />
          <RsPartialNavigationTreeBranch
            :to="{ name: 'SettingsAccounting' }"
            title="Accounting Overview"
            :active="routeIncludes('/settings/accounting')"
          />
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$userHasAccess('custom_fields')"
        class="pl-3 rs-bg-white"
        title="Custom Fields"
        :to="{ name: 'SettingsAccountCustomFields' }"
        :active="routeStartsWith('/settings/account-custom-fields')"
      />

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$currentUser.isSuperUser && $rsGlobals.$env.VUE_APP_ENV === 'demo'"
        class="pl-3"
      >
        <RsPartialNavigationTree
          highlight
          title="Super Admin Settings"
          class="rs-text-white"
          :active="routeStartsWith('/superadmin-account-settings')"
        >
          <RsPartialNavigationTreeBranch
            title="Languages"
            :to="{ name: 'SuperAdminLanguageSettings' }"
            :active="routeEndsWith('/superadmin-account-settings/language')"
          />
          <RsPartialNavigationTreeBranch
            title="Modules"
            :to="{ name: 'SuperAdminSettingsModules' }"
            :active="routeEndsWith('/superadmin-account-settings/modules')"
          />
          <RsPartialNavigationTreeBranch
            title="Integrations"
            :to="{ name: 'SuperAdminSettingsIntegrations' }"
            :active="routeEndsWith('/superadmin-account-settings/integrations')"
          />
          <RsPartialNavigationTreeBranch
            title="Panel Templates"
            :to="{ name: 'SuperAdminSettingsPanelTemplates' }"
            :active="routeEndsWith('/superadmin-account-settings/panel-templates')"
          />
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>
    </RsPartialNavigationTree>
  </RsPartialNavigationContainer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import RsPartialNavigationTree from '@/components/RsPartialNavigationTree.vue';
import RsPartialNavigationContainer from '@/components/RsPartialNavigationContainer.vue';
import RsPartialNavigationTreeBranch from '@/components/RsPartialNavigationTreeBranch.vue';
import { routeEndsWith, routeIncludes, routeStartsWith } from '@/shared/helpers/navigation-active-helpers';
import { getClientLogoURL } from '@/shared/helpers/get-client-logo-url';

export default defineComponent({
  components: {
    RsPartialNavigationTree,
    RsPartialNavigationContainer,
    RsPartialNavigationTreeBranch,
  },

  props: {
    variant: {
      type: String,
      default: 'collapse',
    },

    accountSwitchTreeExpanded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expanded: '',
    };
  },

  computed: {
    user() {
      return this.$rsGlobals.$currentUser;
    },

    logo() {
      return getClientLogoURL(this.$rsGlobals.$currentClient);
    },
  },

  watch: {
    $route: {
      immediate: true,
      deep: true,
      handler() {
        this.handleExpand('');
      },
    },
  },

  methods: {
    routeEndsWith,
    routeIncludes,
    routeStartsWith,
    getClientLogoURL,

    handleExpand(value: string) {
      this.expanded = value;
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.remove-truncated-text {
  white-space: wrap !important;
}

.dropdown-client-info {
  font-size: 14px;
  color: $black;

  img {
    object-fit: cover;
    object-position: top left;
    height: 45px;
    width: 45px;
    border-radius: 25px;
  }
}
</style>
